import { formatPublicSourceLabel } from "@/features/machines/addressing";
import { aggregateSourceState, describePartialState } from "../../multiSource";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import { describeSignalDiagnostic } from "./diagnosticMeaning";
import { formatWidgetNumber, formatWidgetRelative } from "./format";
import { HeroCard, HeroMeaning } from "./HeroCard";
import { describeQuality } from "./quality";

interface MultiValueWidgetProps {
  widget: DashboardWidget;
  /** Valores na ordem das fontes, âncora primeiro. */
  values: ResolvedWidgetValue[];
  /** Rótulo curto por fonte (ex.: "L1", "L2"). Ausente cai no rótulo público. */
  labels?: Record<string, string>;
  editable?: boolean;
  onConfigure?: () => void;
  onGenerateChart?: () => void;
}

/**
 * Painel comparativo: N grandezas da MESMA unidade lado a lado — as três
 * tensões de um multimedidor, as três correntes, três temperaturas de zona.
 *
 * O ponto do widget não é economizar espaço, é permitir a COMPARAÇÃO: com três
 * cards separados o operador precisa ler três números e fazer a conta de
 * cabeça para notar que L2 está 8% abaixo das outras. Aqui o desequilíbrio
 * salta.
 *
 * Por isso o estado é agregado com o pior vencendo, e uma fonte muda é dita em
 * voz alta: um painel trifásico que mostra dois valores bonitos e esconde o
 * terceiro é pior que não existir — o operador conclui que está tudo bem.
 */
export function MultiValueWidget({
  widget,
  values,
  labels,
  editable,
  onConfigure,
  onGenerateChart,
}: MultiValueWidgetProps) {
  const unit = widget.settings.unit?.trim() || null;
  const decimals = widget.settings.decimals ?? 1;
  const state = aggregateSourceState(values);
  const diagnosed = describeSignalDiagnostic(state.diagnostic ?? undefined);
  const partial = describePartialState(state);

  const cardTone =
    state.tone === "danger" ? "danger" : state.tone === "warn" || partial ? "warn" : "ok";

  // O rodape resume a idade da fonte MAIS ANTIGA. Usar a mais nova fazia
  // "Atualizado agora" esconder uma fase cujo ultimo pacote ja venceu.
  const oldestMeasuredAt = values
    .map((value) => value.measuredAt)
    .filter((measuredAt): measuredAt is string => !!measuredAt)
    .sort()
    .at(0);

  // Cada nivel diz o que so ele sabe: o CARD conta quantas fontes estao cegas,
  // e cada ITEM explica por que a dele esta. Com uma fonte falhando os dois
  // repetiam a mesma frase — ruido que rouba a atencao do numero.
  //
  // Quando NENHUMA fonte le (nao e parcial) nao ha contagem util a dar, e ai
  // sim o diagnostico sobe para o card. Vale tambem para diagnosticos que nao
  // tiram a leitura do ar, como subfaixa do AIN.
  const meaning = partial
    ? { tone: "warn" as const, text: partial }
    : state.availableCount === 0 && state.quality !== "live"
      ? { tone: state.quality === "stale" ? "warn" as const : "muted" as const,
          text: describeQuality(state.quality).title }
      : diagnosed;

  return (
    <HeroCard
      title={widget.title}
      caption={unit ? `Comparativo · ${unit}` : "Comparativo"}
      tone={cardTone}
      alarm={widget.settings.alarmEnabled}
      editable={editable}
      onConfigure={onConfigure}
      onGenerateChart={onGenerateChart}
      footnote={
        oldestMeasuredAt
          ? `Leitura mais antiga ${formatWidgetRelative(oldestMeasuredAt) ?? "agora"}`
          : null
      }
    >
      {meaning ? <HeroMeaning tone={meaning.tone}>{meaning.text}</HeroMeaning> : null}

      <div className="w-multi">
        {values.map((value) => {
          const label =
            labels?.[value.source] ??
            formatPublicSourceLabel(value.source, widget.settings.signalKind);
          // Valor indisponível NÃO vira zero: mostra "--" com o rótulo intacto,
          // para a fonte continuar visível na comparação como uma lacuna.
          const readout = value.isAvailable
            ? formatWidgetNumber(value.numericValue, decimals)
            : "--";
          const itemDiagnostic = describeSignalDiagnostic(value.diagnostic);
          return (
            <div
              key={value.source}
              className={
                value.isAvailable ? "w-multi__item" : "w-multi__item w-multi__item--muted"
              }
            >
              <span className="w-multi__label" title={label}>{label}</span>
              <span className="w-multi__value">
                {readout}
                {unit && value.isAvailable ? (
                  <span className="w-multi__unit">{unit}</span>
                ) : null}
              </span>
              {itemDiagnostic ? (
                <span className="w-multi__note" title={itemDiagnostic.text}>
                  {itemDiagnostic.text}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </HeroCard>
  );
}
